import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { download } from "../../lib/download";

const inDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export function BatchFields({ v, set, showCsv = true }) {
  const onFile = (e) => { const file = e.target.files?.[0]; if (file) file.text().then((t) => set({ csv_text: t })); };
  return (
    <>
      <div className="flex gap-4 text-sm" role="radiogroup" aria-label="Batch type">
        {showCsv && <label className="flex items-center gap-2"><input type="radio" checked={v.mode === "csv"} onChange={() => set({ mode: "csv" })} data-testid="batch-mode-csv" /> Named list (CSV: name,email)</label>}
        <label className="flex items-center gap-2"><input type="radio" checked={v.mode === "quantity"} onChange={() => set({ mode: "quantity" })} data-testid="batch-mode-quantity" /> Quantity of unnamed codes</label>
      </div>
      {v.mode === "csv" ? (
        <div>
          <label htmlFor="batch-csv" className="field-label">CSV (name,email per line)</label>
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="mb-2 text-sm" data-testid="batch-csv-file" aria-label="Upload CSV file" />
          <textarea id="batch-csv" rows={6} className="field-input font-mono text-xs" value={v.csv_text} onChange={(e) => set({ csv_text: e.target.value })}
            placeholder={"name,email\nJane Doe,jane@example.com"} data-testid="batch-csv-input" />
        </div>
      ) : (
        <div><label htmlFor="batch-qty" className="field-label">Quantity</label>
          <input id="batch-qty" type="number" min={1} max={1000} className="field-input max-w-[10rem]" value={v.quantity} onChange={(e) => set({ quantity: Number(e.target.value) })} data-testid="batch-quantity-input" /></div>
      )}
      <div><label htmlFor="batch-exp" className="field-label">Expires on</label>
        <input id="batch-exp" type="date" className="field-input max-w-[12rem]" value={v.expires_on} onChange={(e) => set({ expires_on: e.target.value })} data-testid="batch-expiry-input" /></div>
    </>
  );
}

export const emptyBatch = () => ({ label: "", category_id: "", sponsor_id: "", mode: "csv", csv_text: "", quantity: 5, expires_on: inDays(45) });
export const batchBody = (v) => ({ label: v.label, category_id: v.category_id, sponsor_id: v.sponsor_id || null, expires_on: v.expires_on,
  csv_text: v.mode === "csv" ? v.csv_text : "", quantity: v.mode === "quantity" ? v.quantity : 0 });

export function CreateBatchDialog({ onClose, onCreated }) {
  const [v, setV] = useState(emptyBatch());
  const [cats, setCats] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (p) => setV((s) => ({ ...s, ...p }));
  useEffect(() => {
    api.get("/admin/registration-categories").then((r) => { setCats(r.data); const edls = r.data.find((c) => c.key === "edls"); if (edls) set({ category_id: edls.id }); });
    api.get("/admin/sponsors").then((r) => setSponsors(r.data));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/admin/invitation-batches", batchBody(v)); onCreated(data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="create-batch-dialog">
        <DialogHeader><DialogTitle>New invitation batch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><label htmlFor="batch-label" className="field-label">Batch label</label>
            <input id="batch-label" className="field-input" required value={v.label} onChange={(e) => set({ label: e.target.value })} placeholder="EDLS cohort 2026" data-testid="batch-label-input" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label htmlFor="batch-cat" className="field-label">Category</label>
              <select id="batch-cat" className="field-input" required value={v.category_id} onChange={(e) => set({ category_id: e.target.value })} data-testid="batch-category-select">
                <option value="">Select…</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}{c.requires_invitation ? " (invitation only)" : ""}</option>)}</select></div>
            <div><label htmlFor="batch-sponsor" className="field-label">Sponsor (optional; counts against allowance)</label>
              <select id="batch-sponsor" className="field-input" value={v.sponsor_id} onChange={(e) => set({ sponsor_id: e.target.value })} data-testid="batch-sponsor-select">
                <option value="">None</option>{sponsors.map((s) => <option key={s.id} value={s.id}>{s.company}</option>)}</select></div>
          </div>
          <BatchFields v={v} set={set} />
          <button type="submit" className="btn-primary" disabled={busy} data-testid="batch-create-submit-btn">{busy ? "Creating…" : "Create invitations"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function IssuedDialog({ data, onClose }) {
  const b = data.batch;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="issued-dialog">
        <DialogHeader><DialogTitle>{data.invitations.length} invitations created: {b.label}</DialogTitle></DialogHeader>
        <p className="rounded bg-amber-50 p-3 text-sm font-semibold text-amber-900" role="alert">{data.notice}</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-navy btn-sm" onClick={() => download(`/admin/invitation-batches/${b.id}/export.csv`, `invitations-${b.id}.csv`)} data-testid="issued-download-csv-btn">Download CSV</button>
          <button type="button" className="btn-outline btn-sm" onClick={() => download(`/admin/invitation-batches/${b.id}/qr.zip`, `invitation-qr-${b.id}.zip`)} data-testid="issued-download-qr-btn">QR ZIP</button>
          <button type="button" className="btn-outline btn-sm" onClick={() => download(`/admin/invitation-batches/${b.id}/sheet.html`, "", true)} data-testid="issued-open-sheet-btn">Printable QR sheet</button>
        </div>
        <table className="w-full text-left text-xs" data-testid="issued-codes-table">
          <thead><tr className="text-slate-600"><th className="py-1">Name</th><th>Email</th><th>Code</th><th>Link</th></tr></thead>
          <tbody>{data.invitations.map((i) => (
            <tr key={i.id} className="border-t"><td className="py-1">{i.name || "—"}</td><td>{i.email || "—"}</td><td className="font-mono">{i.code}</td>
              <td className="max-w-[18rem] truncate font-mono"><a href={i.link} className="text-ocean underline" target="_blank" rel="noreferrer">{i.link}</a></td></tr>))}</tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
